/**
 * Admin: wallet verification (WITHDRAWAL_REVIEW_PLAN.md §8). When the back office runs in the CRM, the CRM builds
 * the same screens from the same API; Platform Admin then shows them read-only.
 *   VerificationWalletsTab  our receiving addresses per asset (Money → Verification wallets)
 *   WalletCards             one card per client wallet: ask for verification (only while a withdrawal to it is
 *                           under review or on hold), the transfer, approve / reject / escalate / cancel, revoke,
 *                           the withdrawals paid to it and the history. Every step happens on the card.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { fmt, fmtDateTime, VERIFY_DEFAULT_HOURS, WITHDRAWAL_ASSETS, type VerificationWallet, type WithdrawalAssetId } from '@platform/shared';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-8 rounded-md bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const go = 'h-8 rounded-md bg-btn-long px-4 text-xs font-semibold text-btn-long-fg disabled:opacity-50';
const danger = 'h-8 rounded-md border border-down px-3 text-xs font-semibold text-down disabled:opacity-50';
const warnBtn = 'h-8 rounded-md border border-warn px-3 text-xs font-semibold text-warn disabled:opacity-50';
const errOf = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');
const when = (iso: string | null) => (iso ? fmtDateTime(iso) : '—');

// ---------------------------------------------------------------- our verification wallets

export function VerificationWalletsTab() {
  const [rows, setRows] = useState<VerificationWallet[] | null>(null);
  const [meta, setMeta] = useState<{ updatedBy: string | null; updatedAt: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = useCallback(async () => {
    const r = await api.get<{ wallets: VerificationWallet[]; updatedBy: string | null; updatedAt: string | null }>('/api/admin/verification-wallets');
    setRows(r.wallets);
    setMeta(r);
  }, []);
  useEffect(() => {
    void load().catch((e) => setMsg({ ok: false, text: errOf(e) }));
  }, [load]);
  const set = (i: number, patch: Partial<VerificationWallet>) => setRows((rs) => rs!.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.put('/api/admin/verification-wallets', { wallets: rows });
      await load();
      setMsg({ ok: true, text: 'Saved' });
    } catch (e) {
      setMsg({ ok: false, text: errOf(e) });
    } finally {
      setBusy(false);
    }
  };
  if (!rows) return <Spinner />;
  return (
    <div className="flex max-w-[860px] flex-col gap-4">
      <p className="m-0 text-xs text-muted">
        Company-controlled addresses clients send a wallet-verification transfer to. Support picks one, on the client's coin and network, when asking a client to verify a wallet. Each address
        should be used for verification only, so every incoming transfer belongs to a request.
      </p>
      <div className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <div key={r.id || i} className="grid grid-cols-[150px_minmax(0,1fr)_160px_auto] items-center gap-2">
            <select aria-label="Coin and network" value={r.asset} onChange={(e) => set(i, { asset: e.target.value as WithdrawalAssetId })} className={input}>
              {WITHDRAWAL_ASSETS.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
            <input aria-label="Address" value={r.address} onChange={(e) => set(i, { address: e.target.value.trim() })} placeholder="Address" className={cx(input, 'num')} />
            <input aria-label="Label" value={r.label} maxLength={40} onChange={(e) => set(i, { label: e.target.value })} placeholder="TRC20 #1" className={input} />
            <button type="button" className={danger} onClick={() => setRows((rs) => rs!.filter((_, j) => j !== i))}>
              Remove
            </button>
          </div>
        ))}
        {!rows.length && <p className="m-0 text-xs text-muted">No verification wallets yet. Add at least one per coin and network you want to verify.</p>}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className={btn} onClick={() => setRows((rs) => [...rs!, { id: '', asset: 'USDT-TRC20', address: '', label: '' }])}>
          Add a wallet
        </button>
        <button type="button" className={primary} disabled={busy} onClick={() => void save()}>
          {busy ? <Spinner /> : 'Save'}
        </button>
        {meta?.updatedAt && (
          <span className="text-xs text-muted">
            Last saved {when(meta.updatedAt)} by {meta.updatedBy}
          </span>
        )}
      </div>
      {msg && <p className={cx('m-0 text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- wallet cards

interface Found {
  tx_hash: string;
  explorer_url: string;
  from: string | null;
  amount: string;
  amount_matches: boolean;
  sender_matches: boolean | null;
  confirmations: number;
  required_confirmations: number;
  found_at: string;
  found_by: string;
  minutes_after_request: number;
}
interface Verification {
  verification_id: string;
  status: string;
  withdrawal_id: string;
  wallet_type: 'exchange' | 'app' | null;
  coin: string;
  network: string;
  receive_address: string;
  amount: string;
  expires_at: string;
  requested_by: string | null;
  requested_at: string;
  client_sent_at: string | null;
  client_tx_hash: string | null;
  new_details_requested_at: string | null;
  found: Found | null;
  escalate_note: string | null;
  reject_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  credited_usd: string | null;
}
interface WalletCard {
  address_id: string;
  label: string;
  asset: WithdrawalAssetId;
  coin: string;
  network: string;
  address: string;
  wallet_type: 'exchange' | 'app' | null;
  verify_status: string;
  verified_at: string | null;
  verified_by: string | null;
  verify_method: string | null;
  revoke_reason: string | null;
  added_at: string;
  flags: { shared_with_other_client: boolean; paid_withdrawals: number };
  can_request_verification: boolean;
  verification: Verification | null;
  past_verifications: Verification[];
  withdrawals: { withdrawal_id: string; number: string; amount: string; status: string; client_action: string | null; hold_code: string | null; created_at: string }[];
}

const STATUS_CHIP: Record<string, [string, string]> = {
  unverified: ['Not verified', 'bg-raised text-muted'],
  waiting: ['Waiting for transfer', 'bg-warn-bg text-warn'],
  found: ['Transfer found', 'bg-accent-bg text-accent-fg'],
  compliance: ['With Compliance', 'bg-warn-bg text-warn'],
  verified: ['Verified', 'bg-up-bg text-up'],
  rejected: ['Rejected', 'bg-down-bg text-down'],
  revoked: ['Revoked', 'bg-down-bg text-down'],
};
const Chip = ({ s }: { s: string }) => {
  const [t, c] = STATUS_CHIP[s] ?? [s, 'bg-raised text-muted'];
  return <span className={cx('rounded px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap', c)}>{t}</span>;
};
const amt = (v: string, coin: string) => `${fmt(v, coin === 'USDT' ? 2 : 8)} ${coin}`;
const Block = ({ title, aside, children, hl }: { title: string; aside?: ReactNode; children: ReactNode; hl?: boolean }) => (
  <div className={cx('flex flex-col gap-2 rounded-lg border p-3 text-xs', hl ? 'border-warn' : 'border-line')}>
    <div className="flex items-center justify-between gap-2">
      <b className="text-[13px]">{title}</b>
      {aside && <span className="text-muted">{aside}</span>}
    </div>
    {children}
  </div>
);
const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="flex min-w-0 flex-col gap-1 text-[11px] text-muted">
    {label}
    {children}
  </label>
);

/** Every saved wallet of a client, one card each. `readOnly` when the back office runs in the CRM. */
export function WalletCards({ userId, readOnly, focusAddress }: { userId: string; readOnly?: boolean; focusAddress?: string }) {
  const [data, setData] = useState<{ wallets: WalletCard[]; verificationWallets: VerificationWallet[] } | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r = await api.get<{ wallets: WalletCard[]; verificationWallets: VerificationWallet[] }>(`/api/admin/users/${userId}/wallets`);
    setData(r);
    setSel((s) => s ?? r.wallets.find((w) => w.address === focusAddress)?.address_id ?? r.wallets[0]?.address_id ?? null);
  }, [userId, focusAddress]);
  useEffect(() => {
    void load().catch((e) => setErr(errOf(e)));
  }, [load]);
  if (err) return <p className="m-0 text-xs text-down">{err}</p>;
  if (!data) return <Spinner />;
  if (!data.wallets.length) return <p className="m-0 text-xs text-muted">No saved crypto withdrawal wallets.</p>;
  const card = data.wallets.find((w) => w.address_id === sel) ?? data.wallets[0]!;
  return (
    <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
      <div className="flex flex-col gap-2">
        {data.wallets.map((w) => (
          <button
            key={w.address_id}
            type="button"
            onClick={() => setSel(w.address_id)}
            className={cx('flex flex-col gap-1 rounded-lg border p-2 text-left text-xs', w.address_id === card.address_id ? 'border-accent ring-1 ring-accent' : 'border-line')}
          >
            <span className="flex items-center justify-between gap-2">
              <b>{w.label}</b>
              <Chip s={w.verify_status} />
            </span>
            <span className="num text-muted">
              {w.coin} · {w.network} · {w.wallet_type === 'exchange' ? 'Exchange' : w.wallet_type === 'app' ? 'Wallet app' : 'Type not chosen'}
            </span>
          </button>
        ))}
      </div>
      <Card key={card.address_id} c={card} ours={data.verificationWallets} readOnly={!!readOnly} onChanged={() => void load()} />
    </div>
  );
}

function Card({ c, ours, readOnly, onChanged }: { c: WalletCard; ours: VerificationWallet[]; readOnly: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const v = c.verification;
  const act = async (path: string, body: object = {}) => {
    setBusy(path);
    setMsg(null);
    try {
      await api.post(`/api/admin/wallets/${c.address_id}/${path}`, body);
      onChanged();
    } catch (e) {
      setMsg(errOf(e));
    } finally {
      setBusy(null);
    }
  };
  const askable = c.withdrawals.filter((w) => w.status === 'under_review' || w.status === 'compliance');
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <b className="text-sm">
            {c.label} · {c.coin} {c.network} · {c.wallet_type === 'exchange' ? 'Exchange' : c.wallet_type === 'app' ? 'Wallet app' : 'Type not chosen yet'}
          </b>
          <div className="num mt-1 rounded-md border border-line px-2 py-1 text-xs break-all">{c.address}</div>
        </div>
        <Chip s={c.verify_status} />
      </div>
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        <span className={cx('rounded px-1.5 py-0.5', c.flags.shared_with_other_client ? 'bg-down-bg text-down' : 'bg-up-bg text-up')}>
          {c.flags.shared_with_other_client ? 'Another client has saved this address' : 'No other client has saved this address'}
        </span>
        <span className="rounded bg-raised px-1.5 py-0.5 text-muted">
          Added {when(c.added_at)} · {c.flags.paid_withdrawals} paid withdrawal{c.flags.paid_withdrawals === 1 ? '' : 's'}
        </span>
        {c.verify_status === 'verified' && (
          <span className="rounded bg-up-bg px-1.5 py-0.5 text-up">
            Verified {when(c.verified_at)} by {c.verified_by} ({c.verify_method})
          </span>
        )}
        {c.revoke_reason && <span className="rounded bg-down-bg px-1.5 py-0.5 text-down">Revoked: {c.revoke_reason}</span>}
      </div>

      <Block title="Withdrawals to this wallet" hl={askable.length > 0 && !v && c.verify_status !== 'verified'}>
        {c.withdrawals.length ? (
          c.withdrawals.map((w) => (
            <div key={w.withdrawal_id} className="num flex flex-wrap items-center gap-2">
              <b>{w.number}</b> · ${fmt(w.amount, 2)} · <span className="font-sans">{w.status.replace('_', ' ')}</span>
              {w.client_action === 'verify_wallet' && <span className="rounded bg-warn-bg px-1.5 text-warn">client asked to verify</span>}
              <span className="text-muted">{when(w.created_at)}</span>
            </div>
          ))
        ) : (
          <span className="text-muted">None yet.</span>
        )}
        {c.can_request_verification && !readOnly && <RequestForm c={c} ours={ours} withdrawals={askable} busy={busy === 'request-verification'} onSubmit={(b) => void act('request-verification', b)} />}
        {!c.can_request_verification && !v && c.verify_status !== 'verified' && (
          <span className="text-muted">You can ask for verification while a withdrawal to this wallet is under review or on a compliance hold.</span>
        )}
      </Block>

      {v && <OpenVerification c={c} v={v} ours={ours} readOnly={readOnly} busy={busy} act={act} />}

      {c.verify_status === 'verified' && !readOnly && <RevokeForm busy={busy === 'revoke'} onSubmit={(reason) => void act('revoke', { reason })} />}

      {c.past_verifications.length > 0 && (
        <Block title="History">
          {c.past_verifications.map((p) => (
            <div key={p.verification_id} className="num flex flex-wrap gap-2">
              <span className="text-muted">{when(p.requested_at)}</span>
              <span>
                {amt(p.amount, p.coin)} → {short(p.receive_address)}
              </span>
              <span className="font-sans font-semibold">{p.status}</span>
              {p.decided_by && <span className="text-muted">by {p.decided_by}</span>}
              {p.reject_reason && <span className="font-sans">“{p.reject_reason}”</span>}
              {p.credited_usd && <span className="text-up">credited ${fmt(p.credited_usd, 2)}</span>}
              {p.new_details_requested_at && <span className="font-sans text-warn">client asked for new details {when(p.new_details_requested_at)}</span>}
            </div>
          ))}
        </Block>
      )}
      {msg && <p className="m-0 text-xs text-down">{msg}</p>}
    </div>
  );
}

const short = (s: string) => (s.length > 14 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s);

function RequestForm({ c, ours, withdrawals, busy, onSubmit }: { c: WalletCard; ours: VerificationWallet[]; withdrawals: WalletCard['withdrawals']; busy: boolean; onSubmit: (b: object) => void }) {
  const mine = ours.filter((o) => o.asset === c.asset);
  const [wd, setWd] = useState(withdrawals[0]?.withdrawal_id ?? '');
  const [rw, setRw] = useState(mine[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [hours, setHours] = useState(String(VERIFY_DEFAULT_HOURS));
  const [err, setErr] = useState<string | null>(null);
  const suggest = async () => {
    setErr(null);
    try {
      setAmount((await api.get<{ amount: string }>(`/api/admin/wallets/${c.address_id}/suggest-amount?receiveWalletId=${rw}`)).amount);
    } catch (e) {
      setErr(errOf(e));
    }
  };
  if (!mine.length) return <span className="text-warn">Add a {c.coin} {c.network} wallet in Money → Verification wallets first.</span>;
  const to = mine.find((o) => o.id === rw);
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-2">
      <b>Ask the client to verify this wallet</b>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Field label="For withdrawal">
          <select value={wd} onChange={(e) => setWd(e.target.value)} className={input}>
            {withdrawals.map((w) => (
              <option key={w.withdrawal_id} value={w.withdrawal_id}>
                {w.number}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Our verification wallet">
          <select value={rw} onChange={(e) => setRw(e.target.value)} className={input}>
            {mine.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={`Exact amount (${c.coin})`}>
          <span className="flex gap-1">
            <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" className={cx(input, 'num')} />
            <button type="button" className={btn} onClick={() => void suggest()}>
              Suggest
            </button>
          </span>
        </Field>
        <Field label="Deadline (hours)">
          <input value={hours} onChange={(e) => setHours(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={cx(input, 'num')} />
        </Field>
      </div>
      {amount && to && (
        <p className="m-0 rounded-md border-l-2 border-accent bg-accent-bg px-2 py-1.5">
          The client will see: send exactly <b>{amt(amount, c.coin)}</b> from <b>{c.label}</b> to <span className="num">{to.address}</span> on {c.network}, within {hours || VERIFY_DEFAULT_HOURS} hours.
        </p>
      )}
      {err && <span className="text-down">{err}</span>}
      <div>
        <button type="button" className={primary} disabled={busy || !wd || !rw || !amount} onClick={() => onSubmit({ withdrawalId: wd, receiveWalletId: rw, amount, expiresInHours: Number(hours) || VERIFY_DEFAULT_HOURS })}>
          {busy ? <Spinner /> : 'Send request to client'}
        </button>
      </div>
    </div>
  );
}

function OpenVerification({
  c,
  v,
  ours,
  readOnly,
  busy,
  act,
}: {
  c: WalletCard;
  v: Verification;
  ours: VerificationWallet[];
  readOnly: boolean;
  busy: string | null;
  act: (path: string, body?: object) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [rec, setRec] = useState({ txHash: '', from: '', amount: '' });
  const [edit, setEdit] = useState<{ receiveWalletId: string; amount: string; expiresInHours: string } | null>(null);
  const f = v.found;
  const ourLabel = ours.find((o) => o.address === v.receive_address)?.label ?? short(v.receive_address);
  const canApprove = !!f && f.amount_matches && (f.found_by !== 'watcher' || f.confirmations >= f.required_confirmations) && (c.wallet_type !== 'app' || f.sender_matches !== false || v.status === 'escalated');
  return (
    <>
      <Block title="Verification details" aside={`Asked by ${v.requested_by ?? '—'} · ${when(v.requested_at)}`}>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Our verification wallet">
            <span className="num text-fg">{ourLabel}</span>
          </Field>
          <Field label="Exact amount">
            <span className="num text-fg">{amt(v.amount, v.coin)}</span>
          </Field>
          <Field label="Deadline">
            <span className="num text-fg">{when(v.expires_at)}</span>
          </Field>
        </div>
        <span className="text-muted">
          Client: {v.wallet_type ? (v.wallet_type === 'exchange' ? 'keeps it on an exchange' : 'keeps it in a wallet app') : 'has not said where they keep it yet'}
          {v.client_sent_at ? ` · pressed “I've sent it” ${when(v.client_sent_at)}` : ''}
          {v.client_tx_hash ? ` · TXID ${short(v.client_tx_hash)}` : ''}
        </span>
        {!readOnly && v.status === 'waiting' && !edit && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} onClick={() => setEdit({ receiveWalletId: ours.find((o) => o.address === v.receive_address)?.id ?? '', amount: v.amount, expiresInHours: '' })}>
              Change details…
            </button>
            <button type="button" className={danger} disabled={busy === 'verification/cancel'} onClick={() => void act('verification/cancel', { reason: reason.trim() || undefined })}>
              Cancel request
            </button>
          </div>
        )}
        {edit && (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Field label="Our verification wallet">
              <select value={edit.receiveWalletId} onChange={(e) => setEdit({ ...edit, receiveWalletId: e.target.value })} className={input}>
                {ours
                  .filter((o) => o.asset === c.asset)
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={`Amount (${v.coin})`}>
              <input value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} className={cx(input, 'num')} />
            </Field>
            <Field label="New deadline (hours, optional)">
              <input value={edit.expiresInHours} onChange={(e) => setEdit({ ...edit, expiresInHours: e.target.value.replace(/\D/g, '') })} className={cx(input, 'num')} />
            </Field>
            <span className="flex items-end gap-1">
              <button
                type="button"
                className={primary}
                onClick={() => void act('verification/details', { receiveWalletId: edit.receiveWalletId, amount: edit.amount, expiresInHours: edit.expiresInHours ? Number(edit.expiresInHours) : undefined }).then(() => setEdit(null))}
              >
                Save
              </button>
              <button type="button" className={btn} onClick={() => setEdit(null)}>
                Close
              </button>
            </span>
          </div>
        )}
      </Block>

      <Block title={f ? `Transfer found on ${v.network}` : 'Transfer'} aside={f ? `${f.found_by === 'watcher' ? 'Matched automatically' : `Recorded by ${f.found_by}`} · ${when(f.found_at)}` : 'Not found yet'}>
        {f ? (
          <div className="grid grid-cols-3 gap-2">
            <Field label="TXID">
              <a href={f.explorer_url} target="_blank" rel="noreferrer" className="num text-accent-fg">
                {short(f.tx_hash)} ↗
              </a>
            </Field>
            <Field label="Amount received">
              <span className={cx('num', f.amount_matches ? 'text-up' : 'text-down')}>
                {amt(f.amount, v.coin)} {f.amount_matches ? '✓ exact' : '✗ not the amount asked'}
              </span>
            </Field>
            <Field label="Confirmations">
              <span className={cx('num', f.confirmations >= f.required_confirmations ? 'text-up' : 'text-warn')}>
                {f.confirmations} / {f.required_confirmations}
              </span>
            </Field>
            <Field label="Sent from">
              <span className={cx('num', f.sender_matches ? 'text-up' : c.wallet_type === 'exchange' ? 'text-warn' : 'text-down')}>
                {f.from ? short(f.from) : '—'} {f.sender_matches ? '✓ the client’s wallet' : c.wallet_type === 'exchange' ? '(exchange wallet)' : f.from ? '✗ not the client’s wallet' : ''}
              </span>
            </Field>
            <Field label="After request">
              <span className="num text-fg">{f.minutes_after_request} min</span>
            </Field>
            {c.wallet_type === 'exchange' && <span className="col-span-3 text-warn">Exchange wallet: the transfer comes from the exchange's own wallet, so it is matched by exact amount, time and TXID.</span>}
          </div>
        ) : (
          <>
            <span className="text-muted">We check our {ourLabel} address every minute for exactly {amt(v.amount, v.coin)}. No watcher for this network? Record the transfer from the explorer.</span>
            {!readOnly && (
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <input aria-label="TXID" placeholder="TXID" value={rec.txHash} onChange={(e) => setRec({ ...rec, txHash: e.target.value.trim() })} className={cx(input, 'num')} />
                <input aria-label="From" placeholder="Sent from (optional)" value={rec.from} onChange={(e) => setRec({ ...rec, from: e.target.value.trim() })} className={cx(input, 'num')} />
                <input aria-label="Amount received" placeholder={`Amount (${v.coin})`} value={rec.amount} onChange={(e) => setRec({ ...rec, amount: e.target.value })} className={cx(input, 'num')} />
                <button type="button" className={btn} disabled={!rec.txHash || !rec.amount || busy === 'verification/record-transfer'} onClick={() => void act('verification/record-transfer', rec)}>
                  Record transfer
                </button>
              </div>
            )}
          </>
        )}
      </Block>

      {v.escalate_note && (
        <Block title="With Compliance" hl>
          <span>{v.escalate_note}</span>
        </Block>
      )}

      {!readOnly && (
        <Block title="Decision" aside={v.status === 'escalated' ? 'Compliance' : 'Support'}>
          <input aria-label="Reason or note" placeholder="Reason (shown to the client when you reject) or note" value={reason} onChange={(e) => setReason(e.target.value)} className={input} />
          <div className="flex flex-wrap gap-2">
            <button type="button" className={go} disabled={!canApprove || busy === 'verification/approve'} onClick={() => void act('verification/approve', { note: reason.trim() || undefined })}>
              Approve wallet
            </button>
            <button type="button" className={danger} disabled={!reason.trim() || busy === 'verification/reject'} onClick={() => void act('verification/reject', { reason: reason.trim() })}>
              Reject with this reason
            </button>
            {v.status !== 'escalated' && (
              <button type="button" className={warnBtn} disabled={!reason.trim() || busy === 'verification/escalate'} onClick={() => void act('verification/escalate', { reason: reason.trim() })}>
                Escalate to Compliance
              </button>
            )}
          </div>
          {!f && <span className="text-muted">Approve turns on once the transfer is found.</span>}
          {f && !f.amount_matches && <span className="text-down">The amount differs: reject it (what arrived is credited) and ask again with a new amount.</span>}
          {f && f.amount_matches && !canApprove && c.wallet_type === 'app' && f.sender_matches === false && <span className="text-down">Sent from another address: escalate it to Compliance.</span>}
        </Block>
      )}
    </>
  );
}

function RevokeForm({ busy, onSubmit }: { busy: boolean; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Block title="Revoke verification" aside="Compliance">
      <div className="flex gap-2">
        <input aria-label="Reason" placeholder="Reason (account takeover, mule case …)" value={reason} onChange={(e) => setReason(e.target.value)} className={input} />
        <button type="button" className={danger} disabled={busy || !reason.trim()} onClick={() => onSubmit(reason.trim())}>
          Revoke
        </button>
      </div>
    </Block>
  );
}
